/**
 * Evaluating a decision-tree condition against the answers given so far.
 *
 * ## Three-valued, not boolean
 *
 * A condition reading a question that has not been answered is neither true nor false — it is
 * UNKNOWN, and the difference decides the flow. `false` would mean "take the next branch",
 * which silently skips a question whose answer has not been collected yet; `unknown` means
 * "stop, ask first". The old engine's expressions read `undefined` and compared it, so a rule
 * over an unanswered question quietly evaluated false and its branch was never taken — one of
 * the live defects (`ref-not-dominating`) is exactly that, and it is invisible at runtime.
 *
 * So: `true`, `false`, `unknown`, and `ALL`/`ANY` propagate it the way SQL propagates NULL —
 * `ANY` is true as soon as one term is true even if another is unknown, `ALL` is false as soon
 * as one term is false.
 */
import type {
  DocCondition,
  DocOperand,
  DocQuestion,
  DocTerm,
  DecisionTreeDocument,
} from '../utils/wire';

export type Tri = true | false | 'unknown';

/*
 * `AnswerValue` / `Answers` / `isAnswered` come from `answerTypes` — they are NOT redeclared
 * here.
 *
 * They were, and that put four independent copies of "is this answered" in the tree: the two
 * type aliases and an `isAnswered` in this file, plus the predicate inlined a third time in
 * `evalTerm`'s `answered` branch. `flow.ts` imports `Answers` from `answerTypes` and
 * `evaluateCondition` from here, so TS accepted the mix only because the shapes happened to
 * match. Let one drift — `AnswerValue` gains `boolean`, or one spelling starts counting `[]`
 * as answered — and the walk and the evaluator disagree about whether a question was answered,
 * which is the same defect class as the live `ref-not-dominating` bug this engine replaces: a
 * rule reading an answer that is not on the path.
 *
 * Re-exported because callers of this module already import them from here.
 */
export type { AnswerValue, Answers } from './answerTypes';
export { isAnswered } from './answerTypes';

// The re-export above does not bind these names locally; these are what the body uses.
import { isAnswered } from './answerTypes';
import type { Answers } from './answerTypes';

/** What the caller knows besides the answers. */
export interface EvalContext {
  /** Programs already granted to this account, for `granted` terms. */
  grantedPrograms?: ReadonlySet<string>;
}

const and = (xs: Tri[]): Tri =>
  xs.includes(false) ? false : xs.includes('unknown') ? 'unknown' : true;

const or = (xs: Tri[]): Tri =>
  xs.includes(true) ? true : xs.includes('unknown') ? 'unknown' : false;

/* ── Operands ───────────────────────────────────────────────────────────────── */

/**
 * A numeric operand's value, or `undefined` when it cannot be known yet.
 *
 * `fallback` is "whichever of these was actually answered", NOT a logical or: its operands are
 * questions on mutually exclusive branches, so exactly one of them exists on any given path.
 * That is why an unanswered first operand falls through to the second instead of making the
 * whole operand unknown — which is also why it has to be a distinct shape from `or`.
 */
function operandValue(op: DocOperand, answers: Answers): number | undefined {
  if ('question' in op) {
    const v = answers[op.question];
    if (v === undefined || v === '' || Array.isArray(v)) return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  if ('fallback' in op) {
    for (const inner of op.fallback) {
      const v = operandValue(inner, answers);
      if (v !== undefined) return v;
    }
    return undefined;
  }
  if ('sum' in op) {
    let total = 0;
    for (const inner of op.sum) {
      const v = operandValue(inner, answers);
      // One missing addend makes the sum unknown. Treating it as 0 would make
      // `a + b >= 1000` fire on `a` alone, which is a wrong answer rather than a late one.
      if (v === undefined) return undefined;
      total += v;
    }
    return total;
  }
  let product = 1;
  for (const inner of op.product) {
    const v = operandValue(inner, answers);
    if (v === undefined) return undefined;
    product *= v;
  }
  return product;
}

/** Every question an operand reads, for "is this answerable yet" and for the variable picker. */
export function operandRefs(op: DocOperand, out: string[] = []): string[] {
  if ('question' in op) out.push(op.question);
  else for (const inner of 'fallback' in op ? op.fallback : 'sum' in op ? op.sum : op.product) {
    operandRefs(inner, out);
  }
  return out;
}

/* ── Terms ──────────────────────────────────────────────────────────────────── */

function evalTerm(term: DocTerm, answers: Answers, ctx: EvalContext): Tri {
  if ('answered' in term) {
    const v = answers[term.answered.questionId];
    // The only term that is never unknown: "has this been answered" is always decidable.
    // One spelling of this predicate, shared with the walk — see the note at the top.
    return isAnswered(v);
  }

  if ('granted' in term) {
    const granted = ctx.grantedPrograms;
    // Unknown rather than false when the caller did not supply the set: guessing "not
    // granted" would quietly qualify an account for a program it already has.
    if (!granted) return 'unknown';
    return granted.has(term.granted.programName) === term.granted.granted;
  }

  const { left, operator, value } = term.compare;

  if (operator === 'IS' || operator === 'CONTAINS') {
    // Both read an answer directly; arithmetic over a choice makes no sense.
    if (!('question' in left)) return 'unknown';
    const v = answers[left.question];
    /*
     * `isAnswered`, not a hand-rolled emptiness check.
     *
     * This guard used to be `v === undefined || v === ''`, which let an EMPTY multi-select
     * through — so `CONTAINS` on an unanswered multi-select returned `[].includes(x)` ===
     * false, and the branch was skipped as though it had been decided. A fifth spelling of
     * "is this answered", drifting exactly the way the note at the top of this file describes.
     */
    if (!isAnswered(v)) return 'unknown';
    /*
     * Both sides stringified, including the scalar case.
     *
     * `value` is `string | number` and an answer is `string | number | string[]`, so a term
     * authored with the number `5` against an answer stored as `"5"` would make `v === value`
     * false and the branch would be skipped — a silent false, which is the exact failure class
     * this evaluator exists to replace. The array paths already coerced; leaving the scalar
     * path uncoerced meant one operator behaved differently depending on whether the question
     * it read was multi-select.
     */
    if (operator === 'CONTAINS') {
      return Array.isArray(v) ? v.includes(String(value)) : String(v) === String(value);
    }
    // IS on a multi-select answer means "this is the only answer", which is what the live
    // `=== 'a'` tests did against an array-valued answer.
    return Array.isArray(v)
      ? v.length === 1 && v[0] === String(value)
      : String(v) === String(value);
  }

  const n = operandValue(left, answers);
  if (n === undefined) return 'unknown';
  const r = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(r)) return 'unknown';
  switch (operator) {
    case 'EQUALS':
      return n === r;
    case 'GREATER_THAN':
      return n > r;
    case 'GREATER_OR_EQUAL':
      return n >= r;
    case 'LESS_THAN':
      return n < r;
    case 'LESS_OR_EQUAL':
      return n <= r;
  }
}

/** Every question a term reads. */
export function termRefs(term: DocTerm): string[] {
  if ('answered' in term) return [term.answered.questionId];
  if ('granted' in term) return [];
  return operandRefs(term.compare.left);
}

/* ── Conditions ─────────────────────────────────────────────────────────────── */

export function evaluateCondition(
  condition: DocCondition | undefined,
  answers: Answers,
  ctx: EvalContext = {},
): Tri {
  // Absent = unconditional. "Always go here", not "no constraints to check".
  if (!condition) return true;
  const terms = condition.terms.map((t) => evalTerm(t, answers, ctx));
  return condition.join === 'ALL' ? and(terms) : or(terms);
}

export function conditionRefs(condition: DocCondition | undefined): string[] {
  return condition ? condition.terms.flatMap(termRefs) : [];
}

/* ── Helpers over a document ────────────────────────────────────────────────── */

export function questionIndex(doc: DecisionTreeDocument): Map<string, DocQuestion> {
  return new Map(doc.questions.map((q) => [q.questionId, q]));
}

/** Out-transitions of a question, in evaluation order. */
export function outTransitions(doc: DecisionTreeDocument, questionId: string) {
  return doc.transitions
    .filter((t) => t.fromQuestionId === questionId)
    // `priority` is data, not array position — the whole point of the new model. Sorting by it
    // here is what makes first-match-wins independent of how the document happens to be
    // serialised.
    .sort((a, b) => a.priority - b.priority);
}


